import { getEnvVariable } from '@/utils/get-env-variable';

/**
 * Mixpanel Session Replay configuration for the checkout UI.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * WHY THIS IS SAFE FOR A PCI CHECKOUT (the "only UI, never sensitive" promise)
 * ──────────────────────────────────────────────────────────────────────────
 * Session Replay is powered by rrweb, which records the DOM of *our* document.
 * The card-sensitive fields for every provider we support render inside
 * CROSS-ORIGIN iframes injected by the provider SDKs:
 *
 *   - Stripe   → `js.stripe.com` Elements (CardNumber/Expiry/CVC)
 *   - Adyen    → `checkoutshopper-*.adyen.com` secured fields
 *   - Braintree→ `assets.braintreegateway.com` Hosted Fields
 *   - Nuvei    → Nuvei `safecharge`/`cdn` field iframes
 *   - Appcharge→ `fields.*` payment-element iframes (raw-PAN self-managed)
 *
 * rrweb CANNOT read into a cross-origin iframe. Mixpanel only stitches child
 * iframes into a replay when BOTH the parent AND the child page call
 * `mixpanel.init({ record_allowed_iframe_origins: [...] })` with each other's
 * origin allow-listed (SDK >= 2.77). We do NOT control the provider iframe
 * pages and we deliberately DO NOT set `record_allowed_iframe_origins`, so the
 * PAN / CVC / expiry fields are physically never captured — they appear as
 * blank rectangles in the replay. This is the strongest possible guarantee:
 * the sensitive data never leaves the provider's frame into our recording.
 *
 * On top of that, the surrounding chrome we DO own is still protected:
 *   - `maskAllInputs` is ON by default in mixpanel-browser, so the email,
 *     ZIP/postal, promo-code, and any other `<input>` VALUES are masked.
 *   - Sensitive input types (password/email/tel/hidden/autocomplete) are
 *     always masked regardless of config.
 *   - PII rendered as plain TEXT in our own DOM (address autocomplete
 *     suggestions, saved-card last-4) is masked via the `mp-mask` class /
 *     `MP_MASK_TEXT_SELECTOR` below.
 *
 * Everything else — layout, navigation, button states, loaders, errors,
 * prices, product/offer names, which method the user picked, where they
 * dropped off — IS recorded. That is the "most of the UI" we want for
 * funnel/UX debugging.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * WHY THIS DOESN'T HURT PERFORMANCE FOR THE 98% WHO ARE NOT RECORDED
 * ──────────────────────────────────────────────────────────────────────────
 * `record_sessions_percent` makes a sampling dice-roll at init time. The rrweb
 * recorder bundle (`mixpanel-recorder.min.js`, fetched from cdn.mxpnl.com) is
 * only downloaded and executed when a session is sampled IN (or someone calls
 * `start_session_recording()`). Non-sampled visitors never download or run
 * rrweb; they only do a cheap IndexedDB registry check for orphaned data. And
 * `mixpanelService.initialize()` itself is already `deferIdle`'d off the
 * critical first-paint path, so even the sampling decision happens when the
 * main thread is idle.
 */

/** Default share of sessions to record, in percent. `100` = 100% of inits. */
const DEFAULT_RECORD_SESSIONS_PERCENT = 100;

/** Which checkout route is initializing Mixpanel. Selects the sampling env var. */
export type SessionReplayScope = 'checkout' | 'subscriptions';

/** Env var holding the sampling percent for each scope. */
const PERCENT_ENV_KEYS: Record<SessionReplayScope, string> = {
  checkout: 'VITE_SESSION_REPLAY_PERCENT',
  subscriptions: 'VITE_SUBSCRIPTIONS_SESSION_REPLAY_PERCENT',
};

/**
 * CSS class that masks an element's text in replays. `mp-mask` is part of
 * mixpanel-browser's default `record_mask_text_class` regex, so adding this
 * class to any element hides its text content WITHOUT extra config. Use it on
 * any element that renders user PII as text.
 */
export const MP_MASK_CLASS = 'mp-mask';

/**
 * CSS class that fully blocks an element (replaced by an empty box of the same
 * size) in replays. `mp-block` is part of the default `record_block_class`
 * regex. Use it for anything that must never be captured at all, even visually.
 */
export const MP_BLOCK_CLASS = 'mp-block';

/**
 * Defense-in-depth allow-list of selectors whose TEXT is masked, in addition
 * to anything carrying `mp-mask`. These cover the only places the checkout
 * paints customer PII as plain text (input VALUES are already masked by
 * `maskAllInputs`):
 *   - `[data-mp-mask]`            — generic opt-in attribute hook
 *   - `[data-testid^="card-number-"]` — saved-card last-4 rows (SavedCardList)
 */
const MP_MASK_TEXT_SELECTOR = ['[data-mp-mask]', '[data-testid^="card-number-"]'].join(', ');

/**
 * Resolve the configured sampling percent.
 *
 * Session Replay is gated by TWO independent switches — recording only
 * happens when BOTH pass:
 *   1. The `enable_mixpanel_session_recording` feature flag (per-publisher
 *      opt-in), passed in as `sessionRecordingEnabled`. When OFF, returns `0`
 *      unconditionally so no publisher without the flag is ever recorded.
 *   2. PRODUCTION-ONLY environment: every non-production environment (local
 *      dev, staging, on-demand/PR envs) — which is also where ALL
 *      E2E/automation traffic runs — returns `0` so no replay is ever
 *      recorded there. This is what keeps automated runs from generating
 *      Session Replay records that pollute a publisher's Mixpanel data.
 *
 * Event tracking (`mixpanel.track`) is unaffected by either gate — only
 * replay recording is gated here.
 *
 * When both gates pass, reads the scope's percent env var (see
 * `PERCENT_ENV_KEYS`) so ops can dial coverage up/down per checkout type (or
 * kill it with `0`) without a code change. Falls back to
 * `DEFAULT_RECORD_SESSIONS_PERCENT`. Clamped to [0, 100]; non-numeric values
 * fall back to the default rather than silently disabling recording.
 */
export const getRecordSessionsPercent = (
  sessionRecordingEnabled: boolean,
  scope: SessionReplayScope = 'checkout'
): number => {
  if (!sessionRecordingEnabled) {
    return 0;
  }

  if (getEnvVariable('VITE_ENV') !== 'production') {
    return 0;
  }

  const raw = getEnvVariable(PERCENT_ENV_KEYS[scope]);
  if (raw === undefined || raw === null || raw === '') {
    return DEFAULT_RECORD_SESSIONS_PERCENT;
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_RECORD_SESSIONS_PERCENT;
  }
  return Math.min(100, Math.max(0, parsed));
};
/**
 * Session Replay options to merge into `mixpanel.init`. Kept intentionally
 * minimal: only the knobs that change privacy or payload size from their
 * mixpanel-browser defaults are listed; everything else uses the SDK default.
 *
 * `sessionRecordingEnabled` is the resolved `enable_mixpanel_session_recording`
 * feature-flag value; when `false`, `record_sessions_percent` is forced to `0`.
 * `scope` selects which percent env var applies.
 */
export const getSessionReplayConfig = (
  sessionRecordingEnabled: boolean,
  scope: SessionReplayScope = 'checkout'
): Record<string, unknown> => ({
  record_sessions_percent: getRecordSessionsPercent(sessionRecordingEnabled, scope),

  // Record images so the offer art, brand logos and method icons show in the
  // replay (mixpanel blocks `img, video, audio` by default). We still block
  // heavy media — there is none in checkout, but this keeps payload bounded if
  // any is ever added.
  record_block_selector: 'video, audio',

  // Do NOT record console output: it can echo tokens, raw API errors, or PII
  // during debugging. Visual + DOM replay is enough for UX analysis.
  record_console: false,

  // Mask PII text surfaces (in addition to the always-on `mp-mask` class and
  // the always-on input masking).
  record_mask_text_selector: MP_MASK_TEXT_SELECTOR,

  // NOTE: `record_allowed_iframe_origins` is intentionally NOT set. Leaving it
  // unset is what keeps the Stripe/Adyen/Braintree/Nuvei/Appcharge card iframes
  // out of the recording entirely. Do not add provider origins here.
});
